import { useState, useEffect, useRef } from 'react';
import { MobileHeader, HeaderAction } from '../components/MobileHeader';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plane, Train, Car, Hotel, Ticket, Plus, Edit2, Trash2, X, Globe, MapPin, AlertTriangle, LogOut, LogIn } from 'lucide-react';
import { api } from '../api';
import { cn } from '../lib/utils';
import { toast } from 'sonner';

const PASS_TYPES = [
  { value: 'full_pass', label: 'Full Pass' },
  { value: 'party_pass', label: 'Party Pass' },
  { value: 'guest', label: 'Guest' },
  { value: 'artist', label: 'Artist' },
];

export const TRANSPORT_TYPES = [
  { value: 'plane', label: 'Avion', icon: Plane, color: 'text-sky-500', bg: 'bg-sky-50' },
  { value: 'train', label: 'Train', icon: Train, color: 'text-purple-500', bg: 'bg-purple-50' },
  { value: 'car',   label: 'Voiture', icon: Car, color: 'text-green-500', bg: 'bg-green-50' },
];

const HOTEL_TYPES = ['Booking.com', 'Airbnb', 'Particulier'];

const EMPTY_FORM = {
  name: '', date_start: '', date_end: '', city: '', country: '',
  departure_city: '', return_arrival_city: '',
  teach_on_departure_day: false, teach_on_return_day: false,
  optional: false,
  pass_type: '', transport_type: '', transport_company: '', transport_price: '',
  transport_confirmed: false, hotel_name: '', hotel_type: '', hotel_price: '',
  hotel_confirmed: false, ticket_price: '', ticket_confirmed: false, notes: '', color: '#F59E0B',
  // v2.15 — trajet aller / retour séparés, logement complet
  out_confirmed: false, out_date: '', out_time: '', out_info: '', out_ref: '', out_price: '',
  back_confirmed: false, back_date: '', back_time: '', back_info: '', back_ref: '', back_price: '',
  hotel_ref: '', hotel_cancel_until: '',
};
const NUM_FIELDS = ['transport_price', 'hotel_price', 'ticket_price', 'out_price', 'back_price'];

// ─── Conflict warning modal ───────────────────────────────────────────────
export function ConflictModal({ conflicts, onCancel, onForce }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
            <AlertTriangle size={20} className="text-amber-600" />
          </div>
          <div>
            <div className="font-bold text-slate-900">Conflit détecté</div>
            <div className="text-xs text-slate-500">Ce festival chevauche avec :</div>
          </div>
        </div>
        <div className="space-y-1.5 mb-5">
          {conflicts.map((c, i) => (
            <div key={c.id || c.title || i} className="flex items-center gap-2 bg-amber-50 rounded-xl px-3 py-2 text-xs">
              {c.type === 'festival'
                ? <><Ticket size={13} className="text-amber-600 shrink-0" /><span className="font-semibold">{c.name}</span><span className="text-slate-400 ml-auto">{c.date_start} → {c.date_end}</span></>
                : <><Globe size={13} className="text-slate-500 shrink-0" /><span className="font-semibold">{c.title}</span><span className="text-slate-400 ml-auto">{(c.datetime_start || '').slice(0,10)}</span></>
              }
            </div>
          ))}
        </div>
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 h-11 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm">Annuler</button>
          <button onClick={onForce} className="flex-1 h-11 rounded-xl bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm">Forcer quand même</button>
        </div>
      </div>
    </div>
  );
}

// ─── Un trajet (aller ou retour) : réservé ?, date, heure, compagnie / n°, réf., prix ──
function Way({ form, set, p, label }) {
  const field = 'w-full h-9 px-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none';
  const done = !!form[`${p}_confirmed`];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-2" data-testid={`way-${p}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold text-slate-700">{label}</span>
        <label className="flex items-center gap-2 cursor-pointer">
          <span className="text-xs text-slate-500">Réservé</span>
          <div className={cn('w-9 h-5 rounded-full transition-colors cursor-pointer', done ? 'bg-sky-500' : 'bg-slate-200')}
            onClick={() => set(`${p}_confirmed`, !done)} data-testid={`way-${p}-toggle`}>
            <div className={cn('w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-transform', done ? 'translate-x-4 ml-0.5' : 'translate-x-0.5')} />
          </div>
        </label>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {[['date', 'Date', 'date'], ['time', 'Heure', 'time'], ['info', 'Compagnie, n°', 'text'], ['ref', 'Référence', 'text']].map(([k, lab, type]) => (
          <label key={k} className="block">
            <span className="block text-[11px] text-slate-500 mb-0.5">{lab}</span>
            <input type={type} value={form[`${p}_${k}`] || ''} onChange={e => set(`${p}_${k}`, e.target.value)}
              placeholder={k === 'info' ? 'easyJet U2 1234' : ''} className={field} />
          </label>
        ))}
        <label className="block col-span-2">
          <span className="block text-[11px] text-slate-500 mb-0.5">Prix (CHF)</span>
          <input type="number" min="0" value={form[`${p}_price`] ?? ''} onChange={e => set(`${p}_price`, e.target.value)}
            placeholder="0" className={field} data-testid={`way-${p}-price`} />
        </label>
      </div>
    </div>
  );
}

// ─── Full detail modal (transport / hotel / ticket) ──────────────────────
export function FestivalModal({ festival, defaultDate, onClose, onSaved }) {
  const [form, setForm] = useState(festival ? {
    ...EMPTY_FORM, ...Object.fromEntries(Object.entries(festival).filter(([, v]) => v !== null && v !== undefined)),
    transport_price: festival.transport_price ?? '',
    hotel_price: festival.hotel_price ?? '',
    ticket_price: festival.ticket_price ?? '',
    // l'ancien « transport » (une seule ligne) devient l'aller ; « confirmé » vaut pour l'aller et le retour
    out_confirmed: festival.out_confirmed ?? !!festival.transport_confirmed,
    back_confirmed: festival.back_confirmed ?? !!festival.transport_confirmed,
    out_price: festival.out_price ?? festival.transport_price ?? '',
    back_price: festival.back_price ?? '',
    out_info: festival.out_info ?? festival.transport_company ?? '',
  } : { ...EMPTY_FORM, ...(defaultDate ? { date_start: defaultDate, date_end: defaultDate } : {}) });
  const [saving, setSaving] = useState(false);
  const [conflictData, setConflictData] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const totalCost = (parseFloat(form.out_price) || 0) + (parseFloat(form.back_price) || 0)
    + (parseFloat(form.hotel_price) || 0)
    + (parseFloat(form.ticket_price) || 0);

  const doSave = async (force = false) => {
    if (!form.name || !form.date_start || !form.date_end) {
      toast.error('Nom et dates requis'); return;
    }
    setSaving(true);
    try {
      const payload = { ...form, force };
      NUM_FIELDS.forEach(k => { payload[k] = form[k] !== '' && form[k] !== null ? parseFloat(form[k]) : null; });
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      if (festival) {
        await api.put(`/festivals/${festival.id}`, payload);
      } else {
        await api.post('/festivals', payload);
      }
      toast.success(festival ? 'Festival mis à jour' : 'Festival créé');
      onSaved();
    } catch (e) {
      if (e.status === 409 && e.conflicts) {
        setConflictData(e.conflicts);
      } else {
        toast.error(e.message || 'Erreur lors de la sauvegarde');
      }
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center p-0 md:p-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white w-full md:max-w-lg rounded-t-3xl md:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
        data-testid="festival-modal">
        <div className="sticky top-0 bg-white px-5 py-4 border-b border-slate-100 flex items-center justify-between z-10">
          <div className="font-bold text-slate-900">{festival ? 'Modifier le festival' : 'Nouveau festival'}</div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200"><X size={16} /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Name + Color */}
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Nom *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)}
                placeholder="Bachata Sensual Festival Lyon"
                className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                data-testid="festival-name" />
            </div>
            <div className="w-16">
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Couleur</label>
              <input type="color" value={form.color} onChange={e => set('color', e.target.value)}
                className="w-full h-11 rounded-xl border border-slate-200 cursor-pointer px-1" />
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Date début *</label>
              <input type="date" value={form.date_start} onChange={e => set('date_start', e.target.value)}
                className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                data-testid="festival-start" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Date fin *</label>
              <input type="date" value={form.date_end} onChange={e => set('date_end', e.target.value)}
                className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                data-testid="festival-end" />
            </div>
          </div>

          {/* City + Country */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Ville destination</label>
              <input value={form.city} onChange={e => set('city', e.target.value)}
                placeholder="Lyon" className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Pays</label>
              <input value={form.country} onChange={e => set('country', e.target.value)}
                placeholder="France" className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300" />
            </div>
          </div>

          {/* Departure / Return cities */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="flex items-center gap-1.5 text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                <LogOut size={12} className="text-slate-400" /> Départ depuis
              </label>
              <input value={form.departure_city} onChange={e => set('departure_city', e.target.value)}
                placeholder="Genève" className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                data-testid="departure-city" />
            </div>
            <div>
              <label className="flex items-center gap-1.5 text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">
                <LogIn size={12} className="text-slate-400" /> Retour vers
              </label>
              <input value={form.return_arrival_city} onChange={e => set('return_arrival_city', e.target.value)}
                placeholder="Genève" className="w-full h-11 px-3 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-300"
                data-testid="return-city" />
            </div>
          </div>

          {/* Teach on departure/return day */}
          <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 space-y-3">
            <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">Planning — disponibilité cours</p>
            <label className="flex items-start gap-3 cursor-pointer group" data-testid="teach-departure-toggle">
              <div className={cn('mt-0.5 w-9 h-5 rounded-full transition-colors shrink-0', form.teach_on_departure_day ? 'bg-amber-400' : 'bg-slate-200')}
                onClick={() => set('teach_on_departure_day', !form.teach_on_departure_day)}>
                <div className={cn('w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-transform', form.teach_on_departure_day ? 'translate-x-4 ml-0.5' : 'translate-x-0.5')} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">Je peux donner cours le jour du départ</p>
                <p className="text-xs text-slate-500">Le bloc festival commence le lendemain dans le planning</p>
              </div>
            </label>
            <label className="flex items-start gap-3 cursor-pointer group" data-testid="teach-return-toggle">
              <div className={cn('mt-0.5 w-9 h-5 rounded-full transition-colors shrink-0', form.teach_on_return_day ? 'bg-amber-400' : 'bg-slate-200')}
                onClick={() => set('teach_on_return_day', !form.teach_on_return_day)}>
                <div className={cn('w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-transform', form.teach_on_return_day ? 'translate-x-4 ml-0.5' : 'translate-x-0.5')} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">Je peux donner cours le jour du retour</p>
                <p className="text-xs text-slate-500">Le bloc festival se termine la veille dans le planning</p>
              </div>
            </label>
          </div>

          {/* Optional toggle */}
          <div className={cn('rounded-2xl p-4 border transition-colors', form.optional ? 'bg-slate-100 border-slate-200' : 'bg-white border-slate-100')}>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="optional-toggle">
              <div className={cn('mt-0.5 w-9 h-5 rounded-full transition-colors shrink-0', form.optional ? 'bg-slate-400' : 'bg-slate-200')}
                onClick={() => set('optional', !form.optional)}>
                <div className={cn('w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-transform', form.optional ? 'translate-x-4 ml-0.5' : 'translate-x-0.5')} />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800">Festival optionnel / incertain</p>
                <p className="text-xs text-slate-500">Affiché en transparent dans le planning — pratique pour noter un festival avant de confirmer</p>
              </div>
            </label>
          </div>

          {/* Pass */}
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Type de pass</label>
            <div className="flex flex-wrap gap-2">
              {PASS_TYPES.map(pt => (
                <button key={pt.value} type="button"
                  onClick={() => set('pass_type', form.pass_type === pt.value ? '' : pt.value)}
                  className={cn('px-3 py-1.5 rounded-lg text-xs font-bold transition-colors',
                    form.pass_type === pt.value ? 'bg-amber-400 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')}
                  data-testid={`pass-${pt.value}`}>
                  {pt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Trajets (v2.15) : moyen de transport, puis aller et retour séparés */}
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">Trajets</span>
            <div className="flex gap-2">
              {TRANSPORT_TYPES.map(tt => {
                const Icon = tt.icon;
                return (
                  <button key={tt.value} type="button"
                    onClick={() => set('transport_type', form.transport_type === tt.value ? '' : tt.value)}
                    className={cn('flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-bold transition-colors border',
                      form.transport_type === tt.value ? `${tt.bg} ${tt.color} border-transparent` : 'bg-white text-slate-400 border-slate-200 hover:bg-slate-50')}
                    data-testid={`transport-${tt.value}`}>
                    <Icon size={14} /> {tt.label}
                  </button>
                );
              })}
            </div>
            <Way form={form} set={set} p="out" label="Trajet aller" />
            <Way form={form} set={set} p="back" label="Trajet retour" />
          </div>

          {/* Hotel */}
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                <Hotel size={13} className="text-amber-500" /> Hôtel
              </span>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-xs text-slate-500">Réservé</span>
                <div className={cn('w-9 h-5 rounded-full transition-colors cursor-pointer', form.hotel_confirmed ? 'bg-amber-400' : 'bg-slate-200')}
                  onClick={() => set('hotel_confirmed', !form.hotel_confirmed)}>
                  <div className={cn('w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-transform', form.hotel_confirmed ? 'translate-x-4 ml-0.5' : 'translate-x-0.5')} />
                </div>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Nom de l'hôtel</label>
                <input value={form.hotel_name} onChange={e => set('hotel_name', e.target.value)} placeholder="Ibis Lyon Centre"
                  className="w-full h-9 px-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none" />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Plateforme</label>
                <select value={form.hotel_type} onChange={e => set('hotel_type', e.target.value)}
                  className="w-full h-9 px-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none">
                  <option value="">—</option>
                  {HOTEL_TYPES.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Prix nuit(s) (CHF)</label>
              <input type="number" value={form.hotel_price} onChange={e => set('hotel_price', e.target.value)}
                placeholder="0" min="0"
                className="w-full h-9 px-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none"
                data-testid="hotel-price" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Référence</label>
              <input value={form.hotel_ref} onChange={e => set('hotel_ref', e.target.value)}
                className="w-full h-9 px-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Annulation gratuite jusqu'au</label>
              <input type="date" value={form.hotel_cancel_until} onChange={e => set('hotel_cancel_until', e.target.value)}
                className="w-full h-9 px-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none"
                data-testid="hotel-cancel-until" />
            </div>
          </div>

          {/* Ticket */}
          <div className="bg-slate-50 rounded-2xl p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1.5">
                <Ticket size={13} className="text-pink-500" /> Ticket festival
              </span>
              <label className="flex items-center gap-2 cursor-pointer">
                <span className="text-xs text-slate-500">Confirmé</span>
                <div className={cn('w-9 h-5 rounded-full transition-colors cursor-pointer', form.ticket_confirmed ? 'bg-pink-500' : 'bg-slate-200')}
                  onClick={() => set('ticket_confirmed', !form.ticket_confirmed)}>
                  <div className={cn('w-4 h-4 bg-white rounded-full shadow mt-0.5 transition-transform', form.ticket_confirmed ? 'translate-x-4 ml-0.5' : 'translate-x-0.5')} />
                </div>
              </label>
            </div>
            <div>
              <label className="block text-xs text-slate-500 mb-1">Prix ticket (CHF)</label>
              <input type="number" value={form.ticket_price} onChange={e => set('ticket_price', e.target.value)}
                placeholder="0" min="0"
                className="w-full h-9 px-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none"
                data-testid="ticket-price" />
            </div>
          </div>

          {totalCost > 0 && (
            <div className="flex items-center justify-between bg-amber-50 rounded-xl px-4 py-3 border border-amber-100">
              <span className="text-sm font-bold text-amber-700">Coût total estimé</span>
              <span className="text-lg font-extrabold text-amber-600">{totalCost.toFixed(2)} CHF</span>
            </div>
          )}

          {/* Notes */}
          <div>
            <label className="block text-xs font-bold text-slate-600 uppercase tracking-wider mb-1.5">Notes</label>
            <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-amber-300" />
          </div>

          <div className="flex gap-3 pt-1">
            <button onClick={onClose} className="flex-1 h-12 rounded-xl bg-slate-100 text-slate-700 font-bold text-sm">Annuler</button>
            <button onClick={() => doSave(false)} disabled={saving}
              className="flex-1 h-12 rounded-xl bg-amber-400 hover:bg-amber-500 text-white font-bold text-sm disabled:opacity-50"
              data-testid="save-festival-btn">
              {saving ? 'Sauvegarde...' : festival ? 'Mettre à jour' : 'Créer'}
            </button>
          </div>
        </div>
      </div>

      {conflictData && (
        <ConflictModal
          conflicts={conflictData}
          onCancel={() => setConflictData(null)}
          onForce={() => { setConflictData(null); doSave(true); }}
        />
      )}
    </div>
  );
}

// ─── Inline-editable cell ─────────────────────────────────────────────────
function InlineCell({ festivalId, field, value, type = 'text', placeholder = '—', onSaved, className = '' }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(value ?? ''));
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const save = async () => {
    if (!editing) return;
    setEditing(false);
    const newVal = val.trim();
    const original = String(value ?? '');
    if (newVal === original) return;
    setSaving(true);
    try {
      await api.put(`/festivals/${festivalId}`, { [field]: newVal || null });
      onSaved();
    } catch { toast.error('Erreur'); setVal(original); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setEditing(false); setVal(String(value ?? '')); } }}
        className={cn('w-full min-w-0 px-1.5 py-0.5 text-xs border border-amber-400 rounded outline-none bg-amber-50', className)}
        style={{ minWidth: 60 }}
      />
    );
  }

  return (
    <span
      onClick={() => { setVal(String(value ?? '')); setEditing(true); }}
      className={cn('cursor-pointer hover:text-amber-600 transition-colors group relative', saving && 'opacity-50', className)}
      data-testid={`inline-${field}-${festivalId}`}
      title="Cliquer pour modifier"
    >
      {value || <span className="text-slate-300 italic text-[11px]">{placeholder}</span>}
    </span>
  );
}

// ─── Inline select cell ───────────────────────────────────────────────────
function InlineSelectCell({ festivalId, field, value, options, placeholder = '—', onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? '');
  const selectRef = useRef(null);

  useEffect(() => { if (editing) selectRef.current?.focus(); }, [editing]);

  const save = async (newVal) => {
    setEditing(false);
    if (newVal === (value ?? '')) return;
    try {
      await api.put(`/festivals/${festivalId}`, { [field]: newVal || null });
      onSaved();
    } catch { toast.error('Erreur'); }
  };

  if (editing) {
    return (
      <select
        ref={selectRef}
        value={val}
        onChange={e => { setVal(e.target.value); save(e.target.value); }}
        onBlur={() => { setEditing(false); }}
        className="w-full px-1.5 py-0.5 text-xs border border-amber-400 rounded outline-none bg-amber-50"
        autoFocus
      >
        <option value="">—</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  }

  const opt = options.find(o => o.value === value);
  return (
    <span
      onClick={() => setEditing(true)}
      className="cursor-pointer hover:text-amber-600 transition-colors text-xs"
      title="Cliquer pour modifier"
    >
      {opt ? opt.label : <span className="text-slate-300 italic">{placeholder}</span>}
    </span>
  );
}

// ─── Toggle confirmed cell ────────────────────────────────────────────────
export function ConfirmedToggle({ festivalId, field, value, iconOn, iconOff, colorOn, onSaved }) {
  const [saving, setSaving] = useState(false);
  const toggle = async () => {
    setSaving(true);
    try {
      await api.put(`/festivals/${festivalId}`, { [field]: !value });
      onSaved();
    } catch { toast.error('Erreur'); }
    finally { setSaving(false); }
  };
  return (
    <button onClick={toggle} disabled={saving} className={cn('transition-opacity', saving && 'opacity-50')} title={value ? 'Confirmé — cliquer pour annuler' : 'Non confirmé — cliquer pour confirmer'}>
      {value ? iconOn : iconOff}
    </button>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────
const normTxt = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

export default function Festivals() {
  const [festivals, setFestivals] = useState([]);
  const [festQuery, setFestQuery] = useState('');  // recherche (loupe de la barre du titre)
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editFestival, setEditFestival] = useState(null);

  const fetchData = async () => {
    const data = await api.get('/festivals');
    setFestivals(data);
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleDelete = async (f) => {
    if (!window.confirm(`Supprimer "${f.name}" ?`)) return;
    await api.delete(`/festivals/${f.id}`);
    toast.success('Festival supprimé');
    fetchData();
  };

  const shownFestivals = festivals.filter(f => !festQuery.trim()
    || normTxt(`${f.name} ${f.city} ${f.country}`).includes(normTxt(festQuery.trim())));

  const fmtDate = (d) => {
    try { return format(parseISO(d), 'd MMM yy', { locale: fr }); } catch { return d || '—'; }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-full lg:px-4 lg:pt-4 pb-28">
      <MobileHeader title="Festivals" count={festivals.length}
        search={{ value: festQuery, onChange: setFestQuery, placeholder: 'Nom ou ville…', scope: 'festivals' }}
        actions={<HeaderAction icon={Plus} label="Ajouter" primary onClick={() => { setEditFestival(null); setShowModal(true); }} testId="add-festival-btn-mobile" />} />
      {/* Header */}

      {festivals.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <Ticket size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">Aucun festival enregistré</p>
          <p className="text-sm mt-1">Ajoutez vos prochains festivals ici</p>
        </div>
      ) : (
        <>
        {/* Mobile : une carte par festival */}
        <div className="mobile-only grid gap-3 xl:grid-cols-2 items-start">
          {shownFestivals.map(f => {
            const tEmoji = f.transport_type === 'train' ? '🚄' : f.transport_type === 'car' ? '🚗' : '✈';
            const tColor = f.transport_type === 'train' ? 'text-purple-500' : f.transport_type === 'car' ? 'text-green-500' : 'text-sky-500';
            const pass = PASS_TYPES.find(p => p.value === f.pass_type);
            const teachBadge = (on, field) => (
              <button
                onClick={async () => { await api.put(`/festivals/${f.id}`, { [field]: !on }); fetchData(); }}
                className={cn('shrink-0 px-2.5 py-1 rounded-full text-xs font-bold border transition-colors',
                  on ? 'bg-amber-100 text-amber-700 border-amber-200' : 'bg-slate-100 text-slate-400 border-slate-200')}>
                {on ? 'cours ✓' : 'pas de cours'}
              </button>
            );
            return (
              <div key={f.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="festival-card">
                <div className="flex items-start gap-3">
                  <div className="w-3 h-3 rounded-full shrink-0 mt-1.5" style={{ backgroundColor: f.color || '#F59E0B' }} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 text-base">{f.name}</div>
                    <div className="text-sm text-slate-500 mt-0.5">{fmtDate(f.date_start)} → {fmtDate(f.date_end)}</div>
                    {(f.city || f.country) && (
                      <div className="flex items-center gap-1 text-sm text-slate-600 mt-1">
                        <MapPin size={14} className="text-slate-400 shrink-0" />
                        {[f.city, f.country].filter(Boolean).join(', ')}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => { setEditFestival(f); setShowModal(true); }}
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50 active:bg-slate-100"
                      aria-label="Modifier" data-testid={`edit-festival-card-${f.id}`}>
                      <Edit2 size={16} className="text-slate-500" />
                    </button>
                    <button onClick={() => handleDelete(f)}
                      className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 active:bg-red-100"
                      aria-label="Supprimer">
                      <Trash2 size={16} className="text-red-400" />
                    </button>
                  </div>
                </div>

                <div className="mt-3 space-y-2 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-slate-600 min-w-0">
                      <LogOut size={14} className="text-slate-400 shrink-0" />
                      <span className="truncate">Départ : {f.departure_city || '—'}</span>
                    </span>
                    {teachBadge(f.teach_on_departure_day, 'teach_on_departure_day')}
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-slate-600 min-w-0">
                      <LogIn size={14} className="text-slate-400 shrink-0" />
                      <span className="truncate">Retour : {f.return_arrival_city || '—'}</span>
                    </span>
                    {teachBadge(f.teach_on_return_day, 'teach_on_return_day')}
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1">
                    {[
                      { field: 'transport_confirmed', on: <span className={cn('text-lg', tColor)}>{tEmoji}</span>, off: <span className="text-lg text-slate-300 grayscale">{tEmoji}</span> },
                      { field: 'hotel_confirmed', on: <Hotel size={20} className="text-amber-500" />, off: <Hotel size={20} className="text-slate-300" /> },
                      { field: 'ticket_confirmed', on: <Ticket size={20} className="text-pink-500" />, off: <Ticket size={20} className="text-slate-300" /> },
                    ].map(t => (
                      <span key={t.field} className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-50">
                        <ConfirmedToggle festivalId={f.id} field={t.field} value={f[t.field]} iconOn={t.on} iconOff={t.off} onSaved={fetchData} />
                      </span>
                    ))}
                    {pass && <span className="ml-1 text-xs font-semibold text-slate-500 bg-slate-100 px-2 py-1 rounded-lg">{pass.label}</span>}
                  </div>
                  <span className={cn('font-bold whitespace-nowrap', f.total_cost > 0 ? 'text-slate-800' : 'text-slate-300')}>
                    {f.total_cost > 0 ? `${f.total_cost.toFixed(0)} CHF` : '—'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* PC : tableau avec édition directe dans les cellules */}
        <div className="mobile-hidden bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <th className="text-left px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Festival</th>
                  <th className="text-left px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Dates</th>
                  <th className="text-left px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Destination</th>
                  <th className="text-left px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Départ de</th>
                  <th className="text-left px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Retour à</th>
                  <th className="text-left px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Pass</th>
                  <th className="text-center px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Détails</th>
                  <th className="text-right px-3 py-3 text-xs font-bold text-slate-500 uppercase tracking-wide whitespace-nowrap">Total</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {shownFestivals.map((f, idx) => {
                  const transport = TRANSPORT_TYPES.find(t => t.value === f.transport_type);
                  return (
                    <tr key={f.id}
                      className={cn('border-b border-slate-50 hover:bg-slate-50/40 transition-colors group', idx === shownFestivals.length - 1 && 'border-b-0')}
                      data-testid="festival-row">
                      {/* Festival name + color */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: f.color || '#F59E0B' }} />
                          <div className="font-semibold text-slate-900 min-w-0">
                            <InlineCell festivalId={f.id} field="name" value={f.name} placeholder="Nom" onSaved={fetchData} className="font-semibold" />
                          </div>
                        </div>
                      </td>
                      {/* Dates */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1 text-xs text-slate-600">
                          <InlineCell festivalId={f.id} field="date_start" value={f.date_start} type="date" onSaved={fetchData} />
                          <span className="text-slate-300">→</span>
                          <InlineCell festivalId={f.id} field="date_end" value={f.date_end} type="date" onSaved={fetchData} />
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">
                          {fmtDate(f.date_start)} → {fmtDate(f.date_end)}
                        </div>
                      </td>
                      {/* Destination */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 text-xs text-slate-700">
                          <MapPin size={11} className="text-slate-400 shrink-0" />
                          <InlineCell festivalId={f.id} field="city" value={f.city} placeholder="Ville" onSaved={fetchData} />
                          {f.city && f.country && <span className="text-slate-300">,</span>}
                          <InlineCell festivalId={f.id} field="country" value={f.country} placeholder="Pays" onSaved={fetchData} />
                        </div>
                      </td>
                      {/* Departure city + teach toggle */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 text-xs text-slate-600">
                          <LogOut size={11} className="text-slate-400 shrink-0" />
                          <InlineCell festivalId={f.id} field="departure_city" value={f.departure_city} placeholder="—" onSaved={fetchData} />
                          <button
                            onClick={async () => { await api.put(`/festivals/${f.id}`, { teach_on_departure_day: !f.teach_on_departure_day }); fetchData(); }}
                            title={f.teach_on_departure_day ? "Cours ce jour → désactiver" : "Pas de cours ce jour → activer"}
                            className={cn('shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-bold border transition-colors',
                              f.teach_on_departure_day
                                ? 'bg-amber-100 text-amber-700 border-amber-200'
                                : 'bg-slate-100 text-slate-400 border-slate-200 hover:border-amber-200 hover:text-amber-600'
                            )}>
                            cours
                          </button>
                        </div>
                      </td>
                      {/* Return city + teach toggle */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 text-xs text-slate-600">
                          <LogIn size={11} className="text-slate-400 shrink-0" />
                          <InlineCell festivalId={f.id} field="return_arrival_city" value={f.return_arrival_city} placeholder="—" onSaved={fetchData} />
                          <button
                            onClick={async () => { await api.put(`/festivals/${f.id}`, { teach_on_return_day: !f.teach_on_return_day }); fetchData(); }}
                            title={f.teach_on_return_day ? "Cours ce jour → désactiver" : "Pas de cours ce jour → activer"}
                            className={cn('shrink-0 px-1.5 py-0.5 rounded-full text-[9px] font-bold border transition-colors',
                              f.teach_on_return_day
                                ? 'bg-amber-100 text-amber-700 border-amber-200'
                                : 'bg-slate-100 text-slate-400 border-slate-200 hover:border-amber-200 hover:text-amber-600'
                            )}>
                            cours
                          </button>
                        </div>
                      </td>
                      {/* Pass type */}
                      <td className="px-3 py-3">
                        <InlineSelectCell
                          festivalId={f.id} field="pass_type" value={f.pass_type}
                          options={PASS_TYPES} placeholder="—" onSaved={fetchData}
                        />
                      </td>
                      {/* Confirmed icons — always show all 3, plane by default */}
                      <td className="px-3 py-3">
                        <div className="flex items-center justify-center gap-2.5">
                          {(() => {
                            const tEmoji = f.transport_type === 'train' ? '🚄' : f.transport_type === 'car' ? '🚗' : '✈';
                            const tColor = f.transport_type === 'train' ? 'text-purple-500' : f.transport_type === 'car' ? 'text-green-500' : 'text-sky-500';
                            return (
                              <ConfirmedToggle
                                festivalId={f.id} field="transport_confirmed" value={f.transport_confirmed}
                                iconOn={<span className={cn('text-sm', tColor)}>{tEmoji}</span>}
                                iconOff={<span className="text-slate-300 text-sm">{tEmoji}</span>}
                                onSaved={fetchData}
                              />
                            );
                          })()}
                          <ConfirmedToggle
                            festivalId={f.id} field="hotel_confirmed" value={f.hotel_confirmed}
                            iconOn={<Hotel size={14} className="text-amber-500" />}
                            iconOff={<Hotel size={14} className="text-slate-300" />}
                            onSaved={fetchData}
                          />
                          <ConfirmedToggle
                            festivalId={f.id} field="ticket_confirmed" value={f.ticket_confirmed}
                            iconOn={<Ticket size={14} className="text-pink-500" />}
                            iconOff={<Ticket size={14} className="text-slate-300" />}
                            onSaved={fetchData}
                          />
                        </div>
                      </td>
                      {/* Total */}
                      <td className="px-3 py-3 text-right whitespace-nowrap">
                        {f.total_cost > 0
                          ? <span className="font-bold text-slate-700 text-xs">{f.total_cost.toFixed(0)} CHF</span>
                          : <span className="text-slate-300 text-xs">—</span>
                        }
                      </td>
                      {/* Actions */}
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => { setEditFestival(f); setShowModal(true); }}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 transition-colors"
                            title="Modifier les détails"
                            data-testid={`edit-festival-${f.id}`}>
                            <Edit2 size={13} className="text-slate-400" />
                          </button>
                          <button onClick={() => handleDelete(f)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 transition-colors"
                            data-testid={`delete-festival-${f.id}`}>
                            <Trash2 size={13} className="text-red-400" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        </>
      )}

      {showModal && (
        <FestivalModal
          festival={editFestival}
          onClose={() => { setShowModal(false); setEditFestival(null); }}
          onSaved={() => { setShowModal(false); setEditFestival(null); fetchData(); }}
        />
      )}
    </div>
  );
}
